import { includes, isNull } from 'lodash'
import moment, { Duration } from 'moment'

import {
    ManagerNotificationMethod,
    ActorDelayConfigsFragment,
} from '@docpace/shared-graphql/fragments'
import { 
    getAppointmentWaitForExamSeconds,
    getAppointmentWaitForIntakeSeconds,
    getAppointmentTimeInReception,
} from '@docpace/shared-ts-utils'
import {
    AppointmentStatus,
    DelayTimestampsAppointment
} from '@docpace/shared-ts-types'

export interface AppointmentDelayConfigProps {
    notifyTimeInReceptionDelays?: boolean
    notifyTimeInReceptionDelaysMethod?: ManagerNotificationMethod
    notifyTimeInReceptionDelaysAfterMinutes?: number
    ignoreTimeInReceptionEarlyCheckin?: boolean
    notifyWaitForIntakeDelays?: boolean
    notifyWaitForIntakeDelaysMethod?: ManagerNotificationMethod
    notifyWaitForIntakeDelaysAfterMinutes?: number
    notifyWaitForDoctorDelays?: boolean
    notifyWaitForDoctorDelaysMethod?: ManagerNotificationMethod
    notifyWaitForDoctorDelaysAfterMinutes?: number
}

export function isAppointmentAfterTimeInReceptionDelayNotificationThreshold(
    appointment: DelayTimestampsAppointment,
    showAfterMinutes: number | null | undefined,
    ignoreTimeInReceptionEarlyCheckin: boolean
): boolean {
    const timeInReception = getAppointmentTimeInReception(
        appointment,
        true,
        ignoreTimeInReceptionEarlyCheckin
    )
    const isInCorrectState = includes(
        [
            AppointmentStatus.CHECKED_IN,
            AppointmentStatus.WAIT_FOR_INTAKE,
            AppointmentStatus.CHECKING_IN,
        ],
        appointment?.status
    )
    return timeInReception && isInCorrectState && showAfterMinutes
        ? timeInReception?.asMinutes() >= showAfterMinutes
        : false
}

export function isAppointmentAfterIntakeDelayNotificationThreshold(
    appointment: DelayTimestampsAppointment,
    showAfterMinutes: number | null | undefined
): boolean {
    const waitForIntakeDelay = getAppointmentWaitForIntakeSeconds(
        appointment,
        true
    )
    const isInCorrectState = includes(
        [AppointmentStatus.CHECKED_IN, AppointmentStatus.WAIT_FOR_INTAKE],
        appointment?.status
    )
    return waitForIntakeDelay && isInCorrectState && showAfterMinutes
        ? (waitForIntakeDelay/60) >= showAfterMinutes
        : false
}

export function isAppointmentAfterWaitForExamDelayNotificationThreshold(
    appointment: DelayTimestampsAppointment,
    showAfterMinutes: number | null | undefined
): boolean {
    const isInCorrectState = includes(
        [AppointmentStatus.WAIT_FOR_DOCTOR],
        appointment?.status
    )
    const waitForExamDelay = getAppointmentWaitForExamSeconds(
        appointment,
        true
    )
    return (waitForExamDelay && isInCorrectState && showAfterMinutes)
        ? (waitForExamDelay/60) >= showAfterMinutes
        : false
}

export function makeAppointmentDelayClassName(
    appointment: DelayTimestampsAppointment,
    delayConfigs: AppointmentDelayConfigProps
): string | null {
    const {
        notifyTimeInReceptionDelays,
        notifyTimeInReceptionDelaysMethod,
        notifyTimeInReceptionDelaysAfterMinutes,
        ignoreTimeInReceptionEarlyCheckin,
        notifyWaitForIntakeDelays,
        notifyWaitForIntakeDelaysMethod,
        notifyWaitForIntakeDelaysAfterMinutes,
        notifyWaitForDoctorDelays,
        notifyWaitForDoctorDelaysMethod,
        notifyWaitForDoctorDelaysAfterMinutes,
    } = delayConfigs ?? {}

    if (
        notifyTimeInReceptionDelays &&
        notifyTimeInReceptionDelaysMethod === ManagerNotificationMethod.Flash &&
        isAppointmentAfterTimeInReceptionDelayNotificationThreshold(
            appointment,
            notifyTimeInReceptionDelaysAfterMinutes,
            ignoreTimeInReceptionEarlyCheckin ?? false
        )
    ) {
        return 'time-in-reception-delay-row-flash'
    } else if (
        notifyWaitForIntakeDelays &&
        notifyWaitForIntakeDelaysMethod === ManagerNotificationMethod.Flash &&
        isAppointmentAfterIntakeDelayNotificationThreshold(
            appointment,
            notifyWaitForIntakeDelaysAfterMinutes
        )
    ) {
        return 'wait-for-intake-delay-row-flash'
    } else if (
        notifyWaitForDoctorDelays &&
        notifyWaitForDoctorDelaysMethod === ManagerNotificationMethod.Flash &&
        isAppointmentAfterWaitForExamDelayNotificationThreshold(
            appointment,
            notifyWaitForDoctorDelaysAfterMinutes
        )
    ) {
        return 'wait-for-exam-delay-row-flash'
    }
    return null
}

export function makeActorDelayConfigs(actor: ActorDelayConfigsFragment){
    return {
        notifyTimeInReceptionDelays: actor?.managerNotifyTimeInReceptionDelays,
        notifyTimeInReceptionDelaysMethod:
            actor?.managerNotifyTimeInReceptionDelaysMethod,
        notifyTimeInReceptionDelaysAfterMinutes:
            actor?.managerNotifyTimeInReceptionDelaysAfterMinutes,
        ignoreTimeInReceptionEarlyCheckin:
            actor?.managerIgnoreTimeInReceptionEarlyCheckIn,
        notifyWaitForIntakeDelays: actor?.managerNotifyWaitForIntakeDelays,
        notifyWaitForIntakeDelaysMethod:
            actor?.managerNotifyWaitForIntakeDelaysMethod,
        notifyWaitForIntakeDelaysAfterMinutes:
            actor?.managerNotifyWaitForIntakeDelaysAfterMinutes,
        notifyWaitForDoctorDelays: actor?.managerNotifyWaitForDoctorDelays,
        notifyWaitForDoctorDelaysMethod:
            actor?.managerNotifyWaitForDoctorDelaysMethod,
        notifyWaitForDoctorDelaysAfterMinutes:
            actor?.managerNotifyWaitForDoctorDelaysAfterMinutes,
    }
}


/**
 * NOTE: .asMinutes() returns the total minutes (including seconds) while .minutes() returns the minutes in the hour
 * Examples:
 *   moment.duration(330, 'seconds').asMinutes() -> 5.5
 *   moment.duration(330, 'seconds').minutes() -> 5
 *   moment.duration(325, 'minutes').asMinutes() -> 325
 *   moment.duration(325, 'minutes').minutes() -> 25
 */
export interface FormatDelayOptions {
    underOneText?: string
    hideMinutesAfterHour?: boolean
    leadingZeros?: boolean,
    durationType?: 'minutes' | 'seconds'
    timeFormat?: 'h:mm A' | string
}

export function formatDelay(
    inputDuration: Duration | moment.Moment | number | string,
    options: FormatDelayOptions = {}
) {
    const {
        underOneText,
        hideMinutesAfterHour = false,
        leadingZeros = true,
        durationType = 'minutes',
    } = options
    if (isNull(inputDuration)) return ''
    
    const duration: Duration | null = (() => {
        if (typeof inputDuration === 'number') {
            return moment.duration(inputDuration, durationType)
        }
        if (typeof inputDuration === 'string') {
            const parsed = Number(inputDuration)
            if (Number.isNaN(parsed)) return null
            return moment.duration(parsed, durationType)
        }
        if (moment.isDuration(inputDuration)) {
            return moment.duration(inputDuration)
        }
        if (moment.isMoment(inputDuration)) {
            const seconds = inputDuration.diff(moment(), 'seconds')
            return moment.duration(seconds, 'seconds')
        }
        return null
    })()
    const durationInSeconds = duration ? Math.max(duration?.asSeconds(), 0) : null

    if (!duration?.isValid() || durationInSeconds === null) {
        return ''
    } else if (durationInSeconds <= 0) {
        return underOneText ? underOneText : 'None'
    } else if (durationInSeconds < 60) {
        return underOneText ?? `${duration.seconds().toString()}s`
    } else if (duration.asMinutes() < 60) {
        // duration.minutes() works because we make sure the total minutes is less than 60
        let minutes = duration.minutes().toString()
        if (leadingZeros) minutes = minutes.padStart(2, '0')
        return `${minutes}m`
    } else {
        const hours = Math.floor(duration.asHours())
        if (hideMinutesAfterHour === true) return `>${hours}h`
        let minutes = duration.minutes().toString()
        if (leadingZeros) minutes = minutes.padStart(2, '0')
        return `${hours}h ${minutes}m`
    }
}
